<template>
  <div class="rich-text-editor">
    <div v-if="editor" class="editor-toolbar">
      <ion-button
        fill="clear"
        size="small"
        :color="editor.isActive('bold') ? 'primary' : 'medium'"
        title="Bold"
        data-testid="bold-button"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <span class="format-icon bold">B</span>
      </ion-button>
      <ion-button
        fill="clear"
        size="small"
        :color="editor.isActive('italic') ? 'primary' : 'medium'"
        title="Italic"
        data-testid="italic-button"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <span class="format-icon italic">I</span>
      </ion-button>
      <ion-button
        fill="clear"
        size="small"
        :color="editor.isActive('underline') ? 'primary' : 'medium'"
        title="Underline"
        data-testid="underline-button"
        @click="editor.chain().focus().toggleUnderline().run()"
      >
        <span class="format-icon underline">U</span>
      </ion-button>
      <ion-button
        fill="clear"
        size="small"
        :color="editor.isActive('bulletList') ? 'primary' : 'medium'"
        data-testid="bullet-list-button"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <ion-icon slot="icon-only" :icon="listOutline" />
      </ion-button>
      <ion-button
        fill="clear"
        size="small"
        :color="editor.isActive('orderedList') ? 'primary' : 'medium'"
        data-testid="ordered-list-button"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <ion-icon slot="icon-only" :icon="listOutline" class="numbered" />
      </ion-button>
    </div>
    <editor-content :editor="editor" class="editor-content" />
    <div v-if="counter && maxlength" class="character-counter">
      {{ formatNumber(characterCount) }} / {{ formatNumber(maxlength) }}
    </div>
  </div>
</template>

<script setup>
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { IonButton, IonIcon } from '@ionic/vue'
import { computed, onBeforeUnmount, watch } from 'vue'
import CharacterCount from '@tiptap/extension-character-count'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { listOutline } from 'ionicons/icons'

import { formatText } from '@/6_shared/utils/formatText'
import { isHtmlContent } from '@/6_shared/utils/sanitizeHtml'

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  maxlength: { type: Number, default: null },
  counter: { type: Boolean, default: false },
  label: { type: String, default: '' },
  minHeight: { type: String, default: '150px' },
  maxHeight: { type: String, default: '400px' },
})

const emit = defineEmits(['update:modelValue'])

// Convert markdown to HTML if needed
function prepareContent(content) {
  if (!content) return ''

  // If it's already HTML, use it as-is
  if (isHtmlContent(content)) {
    return content
  }

  // Otherwise, convert markdown to HTML
  return formatText(content)
}

const editor = useEditor({
  content: prepareContent(props.modelValue),
  editable: !props.disabled,
  extensions: [
    StarterKit.configure({
      heading: false,
      codeBlock: false,
      code: false,
      blockquote: false,
      horizontalRule: false,
    }),
    Underline,
    CharacterCount.configure({
      limit: props.maxlength,
    }),
    Placeholder.configure({
      placeholder: props.placeholder,
    }),
  ],
  editorProps: {
    attributes: {
      class: 'prose prose-sm focus:outline-none',
    },
  },
  onUpdate: ({ editor }) => {
    let html = editor.getHTML()

    // Clean up excessive line breaks before emitting
    if (html && html !== '<p></p>') {
      // Replace 3+ consecutive empty paragraphs with just 2
      html = html.replace(/(<p><\/p>\s*){3,}/g, '<p></p><p></p>')

      // Replace 3+ consecutive paragraphs with only spaces/breaks
      html = html.replace(/(<p>\s*<\/p>\s*){3,}/g, '<p></p><p></p>')

      // Replace 3+ consecutive paragraphs with &nbsp;
      html = html.replace(/(<p>&nbsp;<\/p>\s*){3,}/g, '<p></p><p></p>')

      // Replace multiple <br> tags with paragraph breaks
      html = html.replace(/(<br\s*\/?>){3,}/g, '<br><br>')
    }

    emit('update:modelValue', html === '<p></p>' ? '' : html)
  },
})

const characterCount = computed(() => {
  if (!editor.value) return 0
  return editor.value.storage.characterCount.characters()
})

function formatNumber(num) {
  return new Intl.NumberFormat().format(num)
}

watch(
  () => props.modelValue,
  (value) => {
    if (editor.value) {
      const preparedContent = prepareContent(value)
      if (editor.value.getHTML() !== preparedContent) {
        editor.value.commands.setContent(preparedContent || '', false)
      }
    }
  },
)

watch(
  () => props.disabled,
  (value) => {
    if (editor.value) {
      editor.value.setEditable(!value)
    }
  },
)

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<style scoped>
.rich-text-editor {
  /* border: 1px solid var(--ion-color-light); */
  border-radius: 8px;
  /* background: var(--ion-color-light-contrast); */
  overflow: hidden;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  padding: 4px;
  background: var(--ion-color-light-tint);
  gap: 4px;
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid var(--ion-color-light);
}

.editor-content {
  min-height: v-bind(minHeight);
  max-height: v-bind(maxHeight);
  overflow-y: auto;
  padding: 12px;
}

.editor-content :deep(.ProseMirror) {
  min-height: 130px;
  outline: none;
}

.editor-content :deep(.ProseMirror p) {
  margin: 0 0 0.5em 0;
}

.editor-content :deep(.ProseMirror p:last-child) {
  margin-bottom: 0;
}

.editor-content :deep(.ProseMirror ul),
.editor-content :deep(.ProseMirror ol) {
  padding-left: 1.5em;
  margin: 0 0 0.5em 0;
}

.editor-content :deep(.ProseMirror li) {
  margin-bottom: 0.25em;
}

.editor-content :deep(.ProseMirror strong) {
  font-weight: bold;
}

.editor-content :deep(.ProseMirror em) {
  font-style: italic;
}

.editor-content :deep(.ProseMirror u) {
  text-decoration: underline;
}

.editor-content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  color: var(--ion-color-medium);
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

.character-counter {
  text-align: right;
  padding: 4px 12px;
  font-size: 0.875rem;
  color: var(--ion-color-medium);
  border-top: 1px solid var(--ion-color-light);
}

.format-icon {
  font-size: 16px;
  font-weight: 600;
  display: inline-block;
  line-height: 1;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.format-icon.bold {
  font-weight: 700;
}

.format-icon.italic {
  font-style: italic;
  font-family: Georgia, serif;
}

.format-icon.underline {
  text-decoration: underline;
}
</style>
